import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { useGlobalConfigContext } from '../../../../global-config'
import { NowGeneralContextProvider } from '../../../../now'
import {
  ProductSearchPagination,
  ProductSearchRefinements,
  ProductSearchRefinementsButton,
  ProductSearchRefinementSortingSelect,
  ProductSearchRefinementStateProvider,
  ProductSearchResultInfo,
} from '../../../../product-search'
import { TENANTS_TO_SHOW_RX_NOT_IN_CATALOG_PRODUCTS } from '../../../../tenant'
import { useStoryblokStory } from '../../../context/StoryblokStoryContext'
import { CmsAlgoliaHits } from '../../CmsAlgoliaHits'
import { StoryblokAlgoliaSearch } from '../../StoryblokAlgoliaSearch/StoryblokAlgoliaSearch'
import { storyblokProductSearchFiltersGetQuery } from '../../StoryblokProductSearchFilters'

import { StoryblokBrandProductSearchBrandLogo } from './StoryblokBrandProductSearchBrandLogo'

export const StoryblokBrandProductSearch: FC = () => {
  const { tenant } = useGlobalConfigContext()

  const { story } = useStoryblokStory()

  if (story.content.component !== 'BrandPage') {
    throw new Error(
      'StoryblokBrandProductSearch can only be used on BrandPage component',
    )
  }

  const { akeneoCategoryCode, brandLogo, title } = story.content

  // products that are available from specific brand
  const brandFiltersAvailableProductsGroup =
    storyblokProductSearchFiltersGetQuery({
      defaultFilters: {
        categoryCodes: akeneoCategoryCode ? [akeneoCategoryCode] : [],
        stockStatusReason: [
          'product.stockStatus.available',
          'product.stockStatus.nl',
        ],
      },
      productSearchFilters: undefined,
    })

  // prescription medicines that are not free from specific brand
  const brandFiltersPrescriptionMedicinesGroup =
    'prescriptionMedicine:true AND NOT stockStatusReason:product.stockStatus.gratis'

  const brandPageFilters = TENANTS_TO_SHOW_RX_NOT_IN_CATALOG_PRODUCTS.has(
    tenant,
  )
    ? `${brandFiltersAvailableProductsGroup} OR ${brandFiltersPrescriptionMedicinesGroup}`
    : brandFiltersAvailableProductsGroup

  /* eslint-disable react/jsx-max-depth -- it's a page with multiple providers */
  return (
    <NowGeneralContextProvider>
      <StoryblokAlgoliaSearch filters={brandPageFilters}>
        <div
          className="flex flex-col gap-3 desktop:gap-6"
          data-qa-id="brand-page-search"
        >
          <div className="flex flex-col gap-3 desktop:gap-6">
            <ProductSearchResultInfo />
            <ProductSearchRefinementsButton className="desktop:hidden" />
          </div>
          <div className="grid gap-4 pt-5 desktop:grid-cols-10 desktop:items-start desktop:gap-6 desktop:pt-0">
            <div className="hidden flex-col gap-6 desktop:col-span-3 desktop:flex">
              <StoryblokBrandProductSearchBrandLogo
                brandLogoRootBlock={brandLogo}
                title={title}
              />
              <div className="flex flex-col gap-4 rounded-s bg-light-primary-medium p-4">
                <h5 className="text-l font-medium">
                  <FormattedMessage id="general.label.filterSearch" />
                </h5>
                <div className="flex flex-col gap-4">
                  <ProductSearchRefinementStateProvider>
                    <ProductSearchRefinementSortingSelect />
                    <ProductSearchRefinements />
                  </ProductSearchRefinementStateProvider>
                </div>
              </div>
            </div>
            <div className="desktop:col-span-7">
              <CmsAlgoliaHits />
              <ProductSearchPagination />
            </div>
          </div>
        </div>
      </StoryblokAlgoliaSearch>
    </NowGeneralContextProvider>
  )
  /* eslint-enable react/jsx-max-depth */
}
